import { exchange, signOff } from "../../lib/webrtc";

/**
 * Receiving a broadcast over WHEP.
 *
 * WHEP is WHIP's mirror image - the same POST of an SDP offer to a URL, except
 * the transceivers are `recvonly` and what comes back is the host's audio and
 * picture as a live `MediaStream`.
 *
 * ## Why not HLS any more
 *
 * Because everything below used to be about a hundred and fifty lines of
 * buffer arithmetic. HLS delivers a broadcast as a playlist of segments, which
 * means the listener is always some seconds behind and those seconds have to be
 * chosen: too few and the buffer underruns and the picture stalls every thirty
 * seconds, too many and the chat runs ahead of the music it is reacting to.
 * Getting it wrong was not theoretical - a listener sitting still watched the
 * stream stop every few seconds, while scrolling the page kept it alive,
 * because the buffer had no cushion and idle tabs get less of the scheduler.
 *
 * WebRTC has no playlist and no segments. Frames arrive as they are produced,
 * the jitter buffer is the browser's problem, and the latency is under a second
 * without anybody choosing a number. The tuning that is gone with it:
 * `liveSyncDuration`, `liveMaxLatencyDuration`, `maxLiveSyncPlaybackRate`, the
 * seek-back on every playlist update, the segment-window setting on the server,
 * and the codec-swap dance for audio the browser would not append.
 *
 * ## Nothing here is a secret
 *
 * The playback URL is public by design - Cloudflare does not put a token on it
 * and every listener needs it - which is why it can live in room state and be
 * handed straight to this. Its opposite number, the publish URL, must never
 * come near this file.
 */

export interface Reception {
  /** The host's stream, ready to attach to a video element. */
  stream: MediaStream;
  /** `connected`, `disconnected`, `failed`, `closed`. */
  onStateChange(handler: (state: RTCPeerConnectionState) => void): void;
  stop(): Promise<void>;
}

/**
 * Subscribe to a broadcast, resolving once media is actually arriving.
 *
 * ## Why this waits for `connected` when publishing does not
 *
 * They are answering different questions. A host pressing "go on air" wants to
 * know the server accepted them, and making them watch a spinner through ICE
 * negotiation reads as a hang. A listener does not care whether the server
 * accepted anything - they want to know whether there is a picture - and
 * resolving early would hand back a `MediaStream` with no frames in it, which
 * presents as a broadcast that is live and black.
 */
export async function watch(url: string): Promise<Reception> {
  const pc = new RTCPeerConnection({ iceServers: [] });

  /*
   * The stream is built here rather than taken from `ontrack`.
   *
   * `ontrack` fires once per track and its `streams[0]` is not guaranteed to be
   * the same object both times, so collecting into one stream we own is the
   * only way to be sure the video element gets the audio *and* the picture.
   */
  const stream = new MediaStream();

  pc.addEventListener("track", (event) => stream.addTrack(event.track));

  // Order matters as little as it ever does, but both must be present before
  // the offer: an offer with no video line gets an answer with no video in it.
  pc.addTransceiver("video", { direction: "recvonly" });
  pc.addTransceiver("audio", { direction: "recvonly" });

  let accepted;

  try {
    accepted = await exchange(url, pc);
    await pc.setRemoteDescription({ type: "answer", sdp: accepted.answer });
  } catch (cause) {
    pc.close();

    throw cause;
  }

  try {
    await connected(pc);
  } catch (cause) {
    pc.close();
    await signOff(accepted.resource);

    throw cause;
  }

  const handlers: ((state: RTCPeerConnectionState) => void)[] = [];

  pc.addEventListener("connectionstatechange", () => {
    for (const handler of handlers) handler(pc.connectionState);
  });

  return {
    stream,
    onStateChange: (handler) => {
      handlers.push(handler);
      handler(pc.connectionState);
    },
    stop: async () => {
      pc.close();
      await signOff(accepted.resource);
    },
  };
}

/** How long to give ICE before calling it a failed connection, in ms. */
const CONNECT_TIMEOUT = 15_000;

/**
 * Resolve when media can flow, reject when it plainly cannot.
 *
 * The timeout exists because `failed` is not guaranteed to arrive. A peer
 * connection that gathers no usable candidate pair can sit in `connecting`
 * indefinitely, which to a listener is the same endless spinner that the old HLS
 * player's silent retry loop produced - and the whole point of the rewrite was
 * to stop producing that.
 */
function connected(pc: RTCPeerConnection): Promise<void> {
  if (pc.connectionState === "connected") return Promise.resolve();

  return new Promise((resolve, reject) => {
    const settle = (outcome: () => void) => {
      pc.removeEventListener("connectionstatechange", onChange);
      clearTimeout(timer);
      outcome();
    };

    const onChange = () => {
      if (pc.connectionState === "connected") settle(resolve);

      if (pc.connectionState === "failed" || pc.connectionState === "closed") {
        settle(() => reject(new Error("The connection to the stream failed.")));
      }
    };

    const timer = setTimeout(
      () => settle(() => reject(new Error("The stream did not start."))),
      CONNECT_TIMEOUT,
    );

    pc.addEventListener("connectionstatechange", onChange);
  });
}
